import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createJob, getConfig, getEffectiveApiKey, SYNC_AUTH_EVENT } from '../../services/syncApi';
import WorkerStatusBanner from './WorkerStatusBanner';
import { DEFAULT_ENVIRONMENT, ENVIRONMENTS, ENVIRONMENT_LABELS } from '../../constants/markets';

const readApiKeyPresent = () => Boolean(getEffectiveApiKey());

const ALL_STAGES = ['create', 'update', 'assign_channel', 'assign_category'];
const ALL_MARKETS = ['AU', 'NZ'];

const parseSkus = (raw) =>
  Array.from(
    new Set(
      raw
        .split(/[\s,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    )
  );

const ManualSync = () => {
  const location = useLocation();
  const presetSkus = location?.state?.presetSkus || null;
  const presetMarket = location?.state?.presetMarket || null;
  const presetEnvironment = location?.state?.presetEnvironment || null;

  const [config, setConfig] = useState({ max_manual_skus: 2000 });
  const [environment, setEnvironment] = useState(presetEnvironment || DEFAULT_ENVIRONMENT);
  const [markets, setMarkets] = useState(presetMarket ? [presetMarket] : ['AU']);
  const [skusText, setSkusText] = useState(presetSkus ? presetSkus.join('\n') : '');
  const [mode, setMode] = useState('mutate');
  const [stages, setStages] = useState(['create', 'update', 'assign_channel', 'assign_category']);
  const [dryRun, setDryRun] = useState(true);
  const [forceFresh, setForceFresh] = useState(false);
  const [limit, setLimit] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState([]); // [{market, status, jobId, error}]
  // localStorage doesn't fire 'storage' events in the same tab that wrote
  // it, so we poll briefly after mount + listen for the 401 event to catch
  // a key being saved-and-reloaded or invalidated by the server.
  const [apiKeyPresent, setApiKeyPresent] = useState(readApiKeyPresent);

  useEffect(() => {
    getConfig().then(setConfig).catch(() => {});
  }, []);

  useEffect(() => {
    const tick = () => setApiKeyPresent(readApiKeyPresent());
    const onStorage = (e) => {
      if (e.key === 'syncApiKey') tick();
    };
    const onAuthErr = () => tick();
    window.addEventListener('storage', onStorage);
    window.addEventListener(SYNC_AUTH_EVENT, onAuthErr);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SYNC_AUTH_EVENT, onAuthErr);
    };
  }, []);

  const parsedSkus = useMemo(() => parseSkus(skusText), [skusText]);
  const skuCount = parsedSkus.length;
  const overCap = skuCount > config.max_manual_skus;

  const toggleMarket = (m) => {
    setMarkets((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  };
  const toggleStage = (s) => {
    setStages((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const onFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setSkusText((prev) => (prev.trim() ? `${prev}\n${text}` : text));
    e.target.value = '';
  };

  const validationError = useMemo(() => {
    if (!apiKeyPresent) return 'API key required — set it via the API key control in the top bar, then click Save.';
    if (markets.length === 0) return 'Select at least one market.';
    if (mode === 'mutate' && stages.length === 0) return 'Mutate mode requires at least one stage.';
    if (overCap) {
      return `Too many SKUs (${skuCount} > ${config.max_manual_skus}). Split into multiple jobs.`;
    }
    return null;
  }, [apiKeyPresent, markets.length, mode, stages.length, overCap, skuCount, config.max_manual_skus]);

  const createWarning = mode === 'mutate' && stages.includes('create')
    ? 'create stage will POST new BigCommerce products with default weight=0, price=0, is_visible=false. Akeneo writeback runs inline after each successful create.'
    : null;

  const submit = async (retryMarkets = null) => {
    setSubmitting(true);
    const targets = retryMarkets || markets;
    if (!retryMarkets) {
      setResults(targets.map((m) => ({ market: m, status: 'pending' })));
    } else {
      setResults((prev) =>
        prev.map((r) => (targets.includes(r.market) ? { ...r, status: 'pending', error: null } : r))
      );
    }

    const payloadBase = {
      environment,
      mode,
      stage_filter: mode === 'mutate' ? stages : [],
      skus: parsedSkus.length ? parsedSkus : null,
      limit: limit ? Number(limit) : null,
      dry_run: dryRun,
      force_fresh: forceFresh,
    };

    const promises = targets.map((market) =>
      createJob({ ...payloadBase, store_code: market }).then(
        (data) => ({ market, status: 'fulfilled', jobId: data.job_id }),
        (err) => ({
          market,
          status: 'rejected',
          error: err?.response?.data?.detail || err.message || 'unknown',
        })
      )
    );
    const settled = await Promise.allSettled(promises);
    const summarised = settled.map((s) => (s.status === 'fulfilled' ? s.value : { status: 'rejected', error: 'promise rejected' }));
    setResults((prev) =>
      prev.map((r) => {
        const found = summarised.find((x) => x.market === r.market);
        return found ? { market: r.market, status: found.status === 'fulfilled' ? 'success' : 'error', jobId: found.jobId, error: found.error } : r;
      })
    );
    setSubmitting(false);
  };

  const failedMarkets = results.filter((r) => r.status === 'error').map((r) => r.market);

  return (
    <div className="sync-page manual-sync">
      <p className="muted">
        Run a one-off compare or mutate job against selected SKUs and markets. Each selected
        market is enqueued as a separate job (one Postgres advisory lock per (market, mode)).
      </p>

      <section className="form-grid">
        <div className="form-group">
          <label>Environment</label>
          <div className="radio-row">
            {(config.environment_values || ENVIRONMENTS).map((env) => (
              <label key={env}>
                <input
                  type="radio"
                  name="environment"
                  checked={environment === env}
                  onChange={() => setEnvironment(env)}
                />
                {ENVIRONMENT_LABELS[env] || env}
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Markets</label>
          <div className="checkbox-row">
            {ALL_MARKETS.map((m) => (
              <label key={m} className="chip">
                <input type="checkbox" checked={markets.includes(m)} onChange={() => toggleMarket(m)} />
                {m}
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Mode</label>
          <div className="radio-row">
            <label>
              <input type="radio" checked={mode === 'compare'} onChange={() => setMode('compare')} />
              compare
            </label>
            <label>
              <input type="radio" checked={mode === 'mutate'} onChange={() => setMode('mutate')} />
              mutate
            </label>
          </div>
        </div>

        {mode === 'mutate' && (
          <div className="form-group">
            <label>Stages</label>
            <div className="checkbox-row">
              {ALL_STAGES.map((s) => (
                <label key={s} className="chip">
                  <input type="checkbox" checked={stages.includes(s)} onChange={() => toggleStage(s)} />
                  {s}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label>
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
            Dry run (no BigCommerce mutations; would_* counters only)
          </label>
        </div>

        <div className="form-group">
          <label>
            <input type="checkbox" checked={forceFresh} onChange={(e) => setForceFresh(e.target.checked)} />
            Force fresh snapshots (refetch from BigCommerce before comparing)
          </label>
        </div>

        <div className="form-group">
          <label>Limit (optional)</label>
          <input
            type="number"
            min="1"
            max="100000"
            placeholder="all eligible"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
          />
        </div>
      </section>

      <section className="form-group skus-block">
        <label>
          SKUs ({skuCount} unique, soft cap {config.max_manual_skus})
          {skuCount === 0 && <span className="muted"> — leave blank to process the entire eligible catalog</span>}
        </label>
        <textarea
          rows={10}
          value={skusText}
          onChange={(e) => setSkusText(e.target.value)}
          placeholder="One SKU per line, or comma/space-separated."
        />
        <div className="upload-row">
          <input type="file" accept=".txt,.csv" onChange={onFileUpload} />
          <span className="muted">Appends file contents to the textarea.</span>
        </div>
        {overCap && (
          <div className="banner error">
            Too many SKUs ({skuCount} &gt; {config.max_manual_skus}). Split into multiple jobs.
          </div>
        )}
      </section>

      {createWarning && (
        <div className="banner warning" role="alert">
          <strong>Heads up: </strong>{createWarning}
        </div>
      )}

      {validationError && (
        <div className="banner error">{validationError}</div>
      )}

      <div className="actions">
        <button
          type="button"
          disabled={submitting || !!validationError}
          onClick={() => submit()}
          title={validationError || 'Enqueue jobs'}
        >
          {submitting ? 'Enqueueing…' : `Enqueue ${markets.length} job${markets.length === 1 ? '' : 's'}`}
        </button>
      </div>

      {results.length > 0 && (
        <section className="results">
          <h2>Results</h2>
          <table>
            <thead>
              <tr><th>Market</th><th>Status</th><th>Job</th><th>Error</th></tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.market}>
                  <td>
                    {r.market}{' '}
                    {r.status === 'success' && <WorkerStatusBanner marketCode={r.market} environment={environment} variant="chip" />}
                  </td>
                  <td className={`status status-${r.status}`}>{r.status}</td>
                  <td>{r.jobId ? <Link to={`/jobs/history/${r.jobId}`}>#{r.jobId}</Link> : '—'}</td>
                  <td className="error-cell">{r.error || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {failedMarkets.length > 0 && (
            <button
              type="button"
              disabled={submitting}
              onClick={() => submit(failedMarkets)}
              className="retry-btn"
            >
              Retry {failedMarkets.length} failed market{failedMarkets.length === 1 ? '' : 's'}
            </button>
          )}
        </section>
      )}
    </div>
  );
};

export default ManualSync;
